import './PendingChangesPanel.scss';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { MdClose } from 'react-icons/md';
import { Appearance, Design } from '../designConstants';
import { IconButton } from '../IconButton';
import { Header } from '../StickyTable';
import { PendingChanges, RowType } from './pending-changes-utils';
import { PendingDeletedRow } from './PendingDeletedRow';
import { PendingEditedRow } from './PendingEditedRow';
import { PendingNewRow } from './PendingNewRow';

interface PendingChangesPanelProps<R> {
  headers: Header[] | ((row: R) => Header[]);
  originalData?: Record<string, R>;
  // Resolves the key to look up a pending row's original in `originalData`. Defaults to the
  // row's `id` field — pass this when rows are keyed some other way (e.g. ModalEditWidget's
  // generic `rowKey`, for row types with no `id` field of their own).
  getRowId?: (row: R) => string;
  pendingChanges: PendingChanges<R>;
  editedRowGroups?: { label: ReactNode; rows: R[] }[];
  hideUnchanged?: boolean;
  pendingRowHeaderRender?: (row: R) => ReactNode;
  setShowPendingChanges: Dispatch<SetStateAction<boolean>>;
  showPendingChanges: boolean;
}

export const PendingChangesPanel = <R extends RowType>({
  headers,
  originalData,
  getRowId = row => String(row.id),
  pendingChanges,
  editedRowGroups,
  hideUnchanged = false,
  pendingRowHeaderRender,
  setShowPendingChanges,
  showPendingChanges
}: PendingChangesPanelProps<R>) => {
  const { editedRows, newRows, deletedRows } = pendingChanges;

  const editedRowsCount = editedRowGroups
    ? editedRowGroups.reduce((total, group) => total + group.rows.length, 0)
    : (editedRows?.length ?? 0);

  const hasPendingChanges =
    editedRowsCount > 0 || Boolean(newRows?.length) || Boolean(deletedRows?.length);
  const moreThanOneTypeOfPendingChanges =
    [editedRowsCount, newRows?.length ?? 0, deletedRows?.length ?? 0].filter(Boolean).length > 1;

  if (!showPendingChanges || !hasPendingChanges) return null;

  const columnsFor = (row: R): Header[] => (typeof headers === 'function' ? headers(row) : headers);

  const renderEditedRow = (editedRow: R) => (
    <PendingEditedRow
      columns={columnsFor(editedRow)}
      key={JSON.stringify(editedRow)}
      newRow={editedRow}
      oldRow={originalData?.[getRowId(editedRow)] ?? editedRow}
      hideUnchanged={hideUnchanged}
      pendingRowHeaderRender={pendingRowHeaderRender}
    />
  );

  return (
    <div className='pending-changes-panel'>
      <div className='pending-changes-top'>
        Pending Changes
        <IconButton
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          className='close-button'
          onClick={() => setShowPendingChanges(false)}
        >
          <MdClose />
        </IconButton>
      </div>
      <div className='pending-changes-body'>
        {originalData &&
          (editedRowGroups
            ? editedRowGroups
                .filter(group => group.rows.length > 0)
                .map((group, i) => (
                  <div className='pending-changes-section' key={i}>
                    <span className='edits-section-title'>{group.label}</span>
                    {group.rows.map(renderEditedRow)}
                  </div>
                ))
            : Boolean(editedRows?.length) && (
                <div className='pending-changes-section'>
                  {moreThanOneTypeOfPendingChanges && (
                    <span className='edits-section-title'>Edited Rows</span>
                  )}
                  {editedRows?.map(renderEditedRow)}
                </div>
              ))}
        {Boolean(newRows?.length) && (
          <div className='pending-changes-section'>
            {moreThanOneTypeOfPendingChanges && (
              <span className='edits-section-title'>New Rows</span>
            )}
            {newRows?.map(newRow => (
              <PendingNewRow
                columns={columnsFor(newRow)}
                key={JSON.stringify(newRow)}
                newRow={newRow}
                pendingRowHeaderRender={pendingRowHeaderRender}
              />
            ))}
          </div>
        )}
        {Boolean(deletedRows?.length) && (
          <div className='pending-changes-section'>
            {moreThanOneTypeOfPendingChanges && (
              <span className='edits-section-title'>Deleted Rows</span>
            )}
            {deletedRows?.map(deletedRow => (
              <PendingDeletedRow
                columns={columnsFor(deletedRow)}
                key={JSON.stringify(deletedRow)}
                oldRow={deletedRow}
                pendingRowHeaderRender={pendingRowHeaderRender}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
